import { DOCUMENT } from '@angular/common';
import {
  Component,
  ComponentFactoryResolver,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BankAccountViewModel } from '@models/bank-account.class';
import { BANNER_ACTIONS, BANNER_TYPE } from '@models/banner.class';
import {
  IContract,
  IContractAccountsCollection,
  Totals,
} from '@models/contract.class';
import { DayTradesViewModel, IDayTradesOrders } from '@models/day-trades.class';
import {
  LegalDocument,
  LEGAL_DOCUMENT_TYPE,
  UserLegalDocument,
} from '@models/documents/legal-documents.class';
import { MODAL_CONTINUE_ACTIONS } from '@models/modal-overlay.class';
import { NotificationMessage } from '@models/notificationMessage.class';
import { Opening, OPENING_TYPE } from '@models/opening.class';
import { TimeWeightedReturn } from '@models/time-weighted-return.class';
import { Store } from '@ngrx/store';
import { Map as MapInmutable } from 'immutable';
import { isEqual } from 'lodash';
import { DeviceDetectorService } from 'ngx-device-detector';
import numeral from 'numeral';
import { combineLatest, EMPTY, forkJoin, Observable, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  flatMap,
  map,
  retry,
  switchMap,
  take,
  tap,
} from 'rxjs/operators';
import {
  AppState,
  CONTRACT_CONFIGURATION,
  EL_PATRON_DEFAULT_CONTRACT,
  LEGAL_PERSONALITY_TYPE,
  TRACKING_EVENTS,
  USER_HAS_NO_BANK_ACCOUNT,
  USER_ROLES,
} from 'src/app';
import { featureFlags } from 'src/app/featureFlags';
import { selectContractsAccountsAccounts } from 'src/app/state/selectors/contract-accounts-selectors';
import {
  selectIsUsa,
  selectTotals,
} from 'src/app/state/selectors/current-contract.selectors';
import { selectFeatureFlag } from 'src/app/state/selectors/feature-flag.selectors';
import { DynamicComponentDirective } from 'src/containers/dynamic-component-loader/dynamic-component.directive';
import {
  ContractsService,
  DocumentsService,
  GeolocationService,
  NotificationService,
  OperationService,
} from 'src/core';
import { MixpanelService } from 'src/core/services/mixpanel.service';
import { SessionService } from 'src/core/services/session.service';
import { StorageService } from 'src/core/services/storage.service';
import { SilentAuth } from 'src/shared/utility/silent-auth.class';

@Component({
  selector: 'hb-main',
  templateUrl: 'mainContainer.component.html',
  styleUrls: ['./mainContainer.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class MainContainerComponent implements OnInit, OnDestroy {
  @ViewChild('upgradeLink') upgradeLink?: ElementRef;
  public showSidebar: boolean;
  /**
   * TODO: [Angular 7] Fix Tradebar Loading
   *
   * It throws ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked.
   */
  // @ViewChildren(DynamicComponentDirective) set factories(
  //   value: QueryList<DynamicComponentDirective>,
  // ) {
  //   const [dynamicComponentHost] = value.toArray();
  //   dynamicComponentHost && this.loadOperationPuppet(dynamicComponentHost);
  // }
  public currentContract: IContract;
  public isGlobalContract = false;
  public userHasNoContracts: boolean = undefined;
  public isLoremAccount = false;
  public isCompatible = true;
  public gbmDashboardUrl: string;
  public canUseChat = true;
  public canTermsConditions = false;
  public isMaintenanceMode = false;
  public enableGeolocationOperationsFeatureFlag$: Observable<boolean>;
  public isServiceInterrupted$: Observable<boolean>;
  public showTermsAndConditions = false;
  public showTradingGlobalTermsAndConditions = false;
  public showNotFundedModal: boolean;

  private currentUsaAgreement: LegalDocument;
  public contractsToAddTerms: string[];
  public isBalanceActionSelected = false;
  public openPriceAlerts$: Observable<boolean>;
  public priceAlertFeatureFlag: boolean;

  public showDayTrades: boolean;
  public dayTrades: IDayTradesOrders[] = new Array<IDayTradesOrders>();
  public dayTradeNextDate: string;
  public isUsaContract: boolean;
  public disabledTradingUsaOperation: boolean;
  public readonly disabledTradingUsaMessage: string =
    'Trading USA está presentando intermitencias, estamos trabajando para restablecer el servicio lo antes posible. Por el momento no podrás operar, sin embargo tu dinero está seguro';
  public readonly disabledTradingMessage: string =
    'Estamos teniendo intermitencias en el sistema y es probable que no veas reflejados los montos. Siéntete tranquilo, tu dinero está resguardado y seguro en GBM+. Estamos trabajando para reestablecer el servicio lo antes posible y evitar que en un futuro vuelva a suceder.';
  public bannerMessage: string;

  public readonly tradingGlobalMessage: string =
    'Comprar fracciones de acciones desde MX$20 en el mercado estadounidense';
  public customInfoBanner: Partial<{
    icon: string | any;
    message: string;
    valid: boolean;
  }> = {};

  public isUjetChat: boolean;
  public isHelpChat: boolean;

  public bannerType = BANNER_TYPE;
  public isBannerVisible: boolean;
  public isTradingGlobalBannerVisible: boolean;
  public isTradingUpgradeModalVisible = false;

  private contractAccountSubscription: Subscription;
  private contractAccountsCollection: IContractAccountsCollection;
  private selectedAccount: any;
  private tradingUsaOperation: boolean;

  private scrollSubscription: Subscription;
  private emptyContractSubscription: Subscription;
  private userGlobalSubscription: Subscription;
  private elPatronDataSubscription: Subscription;
  private logoutSubscription: Subscription;
  private currentContractSubscription: Subscription;
  private dayTradesSubscription: Subscription;
  private ordersSubscription: Subscription;
  private usaTermsAndConditionsSubscription: Subscription;
  private subscriptions = new Subscription();
  private elPatronData: any;
  private bankNumber = 0;
  private bankAccountData: any = undefined;
  private statusAccount: string;
  private statusForSPEI: string;
  private contractId: string;
  private queryParamsSnapshot: any = {};
  private userRoleSubscription: Subscription;
  private silentAuth: SilentAuth;
  private legacyContracts: string[] = [];
  private userCanUseAccountManager: boolean;
  private _userIsComingFromDashboard = false;
  private initializedDayTrades = false;

  /**
   * List of components that have requested the scroll to be blocked,
   * and the number of times they've requested it.
   */
  private scrollBlockers: Map<string, number> = new Map();

  /**
   * Total of scroll requests.
   *
   * When 0, the scroll is unblocked.
   * When >0, the scroll is blocked and needs as many unblock requests to
   * be unblocked.
   */
  private scrollBlockCount = 0;

  constructor(
    @Inject(DOCUMENT) private document: any,
    private router: Router,
    private store: Store<AppState>,
    private sessionService: SessionService,
    private operationService: OperationService,
    private notificationService: NotificationService,
    private storageService: StorageService,
    private deviceService: DeviceDetectorService,
    private route: ActivatedRoute,
    private componentFactoryResolver: ComponentFactoryResolver,
    private contractsService: ContractsService,
    private geolocationService: GeolocationService,
    private documentsService: DocumentsService,
    private readonly mixpanelService: MixpanelService,
  ) {
    this.silentAuth = new SilentAuth(this.authCallback.bind(this));
  }

  ngOnInit() {
    this.backupRouteSnapshot();
    this.cleanUrl();
    this.scanForMaintenanceMode();
    this.scanForenableGeolocationOperations();
    this.scanForServiceInterruptions();
    this.scanForUsaTermsAndConditions();
    this.scanForPriceAlerts();
    this.scanForNotFundedModal();
    this.scanForInfoBannerContent();
    this.initLocaleForCustomFormat();
    !this.isBrowserBlocked() && !this.maintenanceMode() && this.initListeners();
    this.checkIfRouteCanBeActivated();
  }

  private scanForInfoBannerContent() {
    this.subscriptions.add(
      combineLatest([
        this.store.select(selectFeatureFlag('infoBannerContent')),
        this.store.select(selectIsUsa),
      ]).subscribe(([infoBannerContent, isUsa]) => {
        if (!infoBannerContent || !infoBannerContent.length) {
          this.customInfoBanner = {};
          return;
        }

        // Wrap in a try-catch block in case the received info is not a valid JSON
        try {
          const parsedInfoBannerContent = JSON.parse(
            infoBannerContent,
          ) as CustomInfoBannerFF;

          const { message, display } = parsedInfoBannerContent;
          const icon =
            parsedInfoBannerContent.icon && parsedInfoBannerContent.icon.length
              ? parsedInfoBannerContent.icon
              : 'Information-AlertTrianguled';

          if (
            ((!isUsa && !display.includes('trading')) ||
              (isUsa && !display.includes('trading_global'))) &&
            !display.includes('all')
          ) {
            this.customInfoBanner = {};
            return;
          }

          if (!message || !message.length) {
            this.customInfoBanner = {};
            throw new Error(`[InfoBanner] Message is empty or unexistent`);
          }

          const [category, name] = icon.split('-');

          if (!category || !name || !category.length || !name.length) {
            this.customInfoBanner = {};
            throw new Error(
              `[InfoBanner] Icon property doesn't contain a proper format (CamelCaseCategory-CamelCaseIconName)`,
            );
          }

          this.customInfoBanner = {
            message,
            icon: { category, name },
            valid: true,
          };
        } catch (e) {
          console.error(e);
        }
      }),
    );
  }

  private scanForNotFundedModal() {
    this.subscriptions.add(
      this.store
        .select('portfolio')
        .pipe(
          map((value: any) => value.get('timeWeightedReturn')),
          distinctUntilChanged(),
          filter(
            (mapShadowed: any) =>
              MapInmutable.isMap(mapShadowed) && mapShadowed.size > 0,
          ),
          map((twr) => twr.toJS() as TimeWeightedReturn),
          flatMap((twr) =>
            this.store.select(selectTotals).pipe(
              filter(Boolean),
              tap((e: Totals) => {
                if (e.buyingPower === 0 && twr.todayPortfolioValue === 0) {
                  this.showNotFundedModal = true;
                }
              }),
            ),
          ),
        )
        .subscribe(),
    );
  }

  private scanForUsaTermsAndConditions() {
    this.usaTermsAndConditionsSubscription = this.store
      .select(selectIsUsa)
      .pipe(
        switchMap((isUsa) =>
          isUsa ? this.checkForTradingUsaAgreements$() : EMPTY,
        ),
      )
      .subscribe();
  }

  ngOnDestroy() {
    [
      this.scrollSubscription,
      this.emptyContractSubscription,
      this.logoutSubscription,
      this.userGlobalSubscription,
      this.userRoleSubscription,
      this.currentContractSubscription,
      this.elPatronDataSubscription,
      this.contractAccountSubscription,
      this.ordersSubscription,
      this.dayTradesSubscription,
      this.usaTermsAndConditionsSubscription,
    ]
      .filter((subscription) => !!subscription)
      .forEach((subscription) => subscription.unsubscribe());
    this.subscriptions.unsubscribe();
  }

  private checkForTradingUsaAgreements$() {
    return this.getTradingUsaDocuments$().pipe(
      map(({ lastUsaTermsAndConditions, userAgreedDocuments }) => {
        this.currentUsaAgreement = lastUsaTermsAndConditions;

        this.showTradingGlobalTermsAndConditions = !userAgreedDocuments.some(
          ({ version }) => version === lastUsaTermsAndConditions.version,
        );
      }),
    );
  }

  private getTradingUsaDocuments$(): Observable<{
    lastUsaTermsAndConditions: LegalDocument;
    userAgreedDocuments: UserLegalDocument[];
  }> {
    return forkJoin(
      this.documentsService.getLegalDocuments$(),
      this.documentsService.getUserLegalDocuments$(),
    ).pipe(
      map(([allLegalDocuments, userAgreedDocuments]) => ({
        lastUsaTermsAndConditions: allLegalDocuments.find(
          ({ type }) =>
            type === LEGAL_DOCUMENT_TYPE.tradingUsaTermsAndConditions,
        ),
        userAgreedDocuments: userAgreedDocuments.filter(
          ({ type }) =>
            type === LEGAL_DOCUMENT_TYPE.tradingUsaTermsAndConditions,
        ),
      })),
    );
  }

  public handleAcceptTradingUsaAgreements() {
    this.documentsService
      .acceptLegalDocument$(this.currentUsaAgreement)
      .subscribe(
        () => {
          this.showTradingGlobalTermsAndConditions = false;
        },
        () => {
          this.notificationService.showError({
            message: 'Ocurrió un error, por favor intenta de nuevo más tarde.',
          });
        },
      );
  }

  public acceptTerms() {
    this.showTermsAndConditions = false;
  }

  public get userHasDashboard() {
    return this.isLoremAccount && this._userIsComingFromDashboard;
  }

  public handleTradingGlobalBannerAction(action: string) {
    if (action === BANNER_ACTIONS.close) {
      this.isTradingGlobalBannerVisible = false;
      this.mixpanelService.track(
        TRACKING_EVENTS.user_ignores_banner_tg_mx.event,
        TRACKING_EVENTS.user_ignores_banner_tg_mx.success('Portfolio'),
      );
      this.storageService.setItem('is_upgrade_banner_closed', true);
    } else {
      this.mixpanelService.track(
        TRACKING_EVENTS.user_clicks_banner_tg_mx.event,
        TRACKING_EVENTS.user_clicks_banner_tg_mx.success('Portfolio'),
      );
      this.isTradingUpgradeModalVisible = true;
    }
  }

  public handleUpgradeModal(type: MODAL_CONTINUE_ACTIONS): void {
    this.isTradingUpgradeModalVisible =
      type === MODAL_CONTINUE_ACTIONS.continue;
    if (type === MODAL_CONTINUE_ACTIONS.continue) {
      this.handleUpgradeBlackLink();
    }
  }

  private initListeners() {
    this.receiveContract();
    this.listenEmptyContracts();
    this.listenScrollBlocked();
    this.listenElPatronData();
    this.listenUserGlobal();
    this.listenCurrentContract();
    this.listenForUserRole();
    this.scanForAllContracts();
    this.scanForTradingUsaOperation();
    this.scanForChatOptions();
    this.refreshWindowSessionWhenMultipleLogin();
  }

  private refreshWindowSessionWhenMultipleLogin(): void {
    window.addEventListener('storage', (event) => {
      if (event.key === `${this.storageService.getPrefix()}-session`) {
        if (event.oldValue && !event.newValue) {
          location.reload();
        }
      }
    });
  }
  private scanForChatOptions() {
    this.store
      .select(selectFeatureFlag('ujetChatHB'))
      .pipe(filter((enableUjetChat: boolean) => enableUjetChat !== undefined))
      .subscribe((enableUjetChat: boolean) => {
        this.isUjetChat = enableUjetChat;
      });

    this.store
      .select(selectFeatureFlag('helpChat'))
      .pipe(filter((enableHelpChat: boolean) => enableHelpChat !== undefined))
      .subscribe((enableHelpChat: boolean) => {
        this.isHelpChat = enableHelpChat;
      });
  }

  private loadOperationPuppet(dynamicComponentHost: DynamicComponentDirective) {
    /**
     * TODO: [Angular 7] Fix Tradebar Loading
     *
     * It throws ExpressionChangedAfterItHasBeenCheckedError: Expression has changed after it was checked.
     */
    // const componentFactory = this.componentFactoryResolver.resolveComponentFactory(
    //   TradeBarComponent,
    // );
    // const viewContainerRef = dynamicComponentHost.viewContainerRef;
    // viewContainerRef.clear();
    // viewContainerRef.createComponent(componentFactory);
  }

  private initLocaleForCustomFormat() {
    const num: any = numeral;
    if (
      num.options !== undefined &&
      num.options.currentLocale !== 'homebroker-format'
    ) {
      num.register('locale', 'homebroker-format', {
        delimiters: {
          thousands: ',',
          decimal: '.',
        },
        abbreviations: {
          thousand: 'k',
          million: 'm',
          billion: 'b',
          trillion: 't',
        },
        ordinal(numero: number) {
          return numero === 1 ? 'er' : 'ème';
        },
        currency: {
          symbol: '$',
        },
      });
    }
  }

  private listenEmptyContracts() {
    this.emptyContractSubscription = this.store
      .select('emptyContracts')
      .pipe(filter((value: any) => value !== undefined))
      .subscribe((emptyContracts: any) => {
        this.userHasNoContracts = emptyContracts;
        if (emptyContracts) {
          this.sessionService.logout(false);
        }
      });
  }

  private listenScrollBlocked() {
    const body: HTMLBodyElement = window.document.querySelector('body');

    body.classList.add('onapp');

    this.scrollSubscription = this.store
      .select('scrollBlocked')
      .pipe(
        map((payload: ScrollStorePayload) =>
          payload.toJS ? payload.toJS() : undefined,
        ),
        filter(Boolean),
      )
      .subscribe(({ block, source, forceOwnUnblock }) => {
        this.handleScrollBlockingEvents(block, source, body, forceOwnUnblock);
      });
  }

  /**
   * Handle store events that want to block/unblock the body scroll
   *
   * This will keep track of who's asking (source) to block/unblock the
   * scroll, and how many times they've requested each event, so **it only
   * updates it when it's correct.**
   *
   * A source cannot ask the unblock the scroll if it didn't block it first.
   * This prevents components that trigger an unblock, to interrupt another
   * component that needed to be rendered with blocked scroll.
   *  -
   * ## Notes
   *
   * If some components (or sources, in general) relied in dispatching
   * an "UNBLOCK" when destroying, will fail if during its lifetime, it
   * dispatched more "BLOCKS" than "UNBLOCKS".
   *
   * @param block is the event requesting to block the scroll?
   * @param source who's emitting the request
   * @param body body element to add/remove the no-scroll class
   * @param forceOwnUnblock will remove all the block requests of this source
   */
  private handleScrollBlockingEvents(
    block: boolean,
    source: string,
    body: HTMLBodyElement,
    forceOwnUnblock: boolean = true,
  ) {
    block
      ? this.handleScrollBlockRequest(source)
      : this.handleScrollUnblockRequest(source, forceOwnUnblock);

    // Prevent scrollBlockCount from being negative
    this.scrollBlockCount = Math.max(this.scrollBlockCount, 0);

    if (this.scrollBlockCount === 0) {
      body.classList.remove('no-scroll');
    } else {
      body.classList.add('no-scroll');
    }
  }

  private handleScrollBlockRequest(source: string) {
    this.scrollBlockers.set(source, (this.scrollBlockers.get(source) || 0) + 1);
    this.scrollBlockCount++;
  }

  /**
   * Update the flags when a source requests to unblock the scroll
   * @param source that requested to unblock the scroll
   */
  private handleScrollUnblockRequest(
    source: string,
    forceOwnUnblock?: boolean,
  ) {
    // A source that is not in the list of blockers can't request to unblock
    if (!this.scrollBlockers.has(source)) {
      return;
    }

    // Decrease the **INDIVIDUAL** blockerCount by one, and delete if it would result in 0
    const blockerCount = this.scrollBlockers.get(source);
    if (blockerCount === 1 || forceOwnUnblock) {
      this.scrollBlockers.delete(source);
      this.scrollBlockCount -= blockerCount;
      return;
    }

    this.scrollBlockers.set(source, blockerCount - 1);
    this.scrollBlockCount--;
  }

  private listenElPatronData() {
    this.elPatronDataSubscription = this.store
      .select('elPatronData')
      .pipe(
        map((value: any) => value.toJS()),
        filter((value: any) => value.URL_SIGNIN !== undefined),
        take(1),
      )
      .subscribe((value: any) => {
        this.elPatronData = value;

        this.gbmDashboardUrl = this.elPatronData.GBMCOM_DASHBOARD;
        this.startAuthenticationProcess();
      });
  }

  private listenUserGlobal() {
    this.userGlobalSubscription = this.store
      .select('userGlobal')
      .subscribe((value: any) => {
        this.isGlobalContract = value;
      });
  }

  private listenCurrentContract() {
    this.currentContractSubscription = this.store
      .select('currentContract')
      .pipe(
        map((values: any) => values.toJS()),
        filter(
          (currentContract: any) =>
            currentContract && currentContract.contractID,
        ),
        distinctUntilChanged((a: IContract, b: IContract) => {
          const isSameContract = a.contractID === b.contractID;
          const isSameLoremAccount = a.isLoremAccount === b.isLoremAccount;
          return isSameContract && isSameLoremAccount;
        }),
      )
      .subscribe((currentContract: IContract) => {
        const { contractID, isLoremAccount, isDiscretional, isUsa } =
          currentContract;
        this.currentContract = currentContract;
        this.contractId = contractID;
        this.isUsaContract = isUsa;
        this.showDayTrades = false;
        this.setTradingUsaOperation();

        this.isLoremAccount = isLoremAccount;
        this.scanForWithdrawalAccount(contractID);
        this.canUseChat = !isDiscretional;
        this.getContractAccount();
      });
  }

  private maintenanceMode() {
    return (this.isMaintenanceMode =
      featureFlags.maintenanceModeHB.isEnabled());
  }

  private redirectToPatron(where: number = 0) {
    const WAIT_TIME = 50;
    let redirectTo: string;

    if (
      this.elPatronData !== undefined &&
      this.elPatronData.URL_SIGNIN !== undefined
    ) {
      switch (where) {
        case 1: {
          this.sessionService.logout(false);
          redirectTo = this.elPatronData.URL_SIGNOUT;
          break;
        }
        case 2: {
          redirectTo = this.elPatronData.URL_SET_MFA;
          break;
        }
        default: {
          redirectTo = this.elPatronData.URL_SIGNIN;
          break;
        }
      }
      this.document.location.href = redirectTo;
    } else {
      setTimeout(() => {
        this.redirectToPatron(where);
      }, WAIT_TIME);
    }
  }

  private showError(notificationMessage: NotificationMessage) {
    this.notificationService.showError(notificationMessage);
  }

  /**
   * TODO: Add this dispatch execution to the
   * gateway process when a not expired token is not longer valid.
   */
  // showIframeCommunication() {
  //     this.store.dispatch({
  //       type: EL_PATRON_SHOW_IFRAME,
  //       payload: true,
  //     });
  //   }

  private isBrowserBlocked() {
    const IE = 'ie';
    const EDGE = 'ms-edge';
    const deviceInfo = this.deviceService.getDeviceInfo();

    if (deviceInfo.browser === IE || deviceInfo.browser === EDGE) {
      this.isCompatible = false;
      return true;
    } else {
      return false;
    }
  }

  private receiveContract() {
    let defaultContract: string =
      this.sessionService.getCurrentContract() ||
      CONTRACT_CONFIGURATION.DEFAULT;

    if (this.queryParamsSnapshot.contract_id) {
      defaultContract = this.queryParamsSnapshot.contract_id;
    }

    this.store.dispatch({
      type: EL_PATRON_DEFAULT_CONTRACT,
      payload: defaultContract,
    });

    this.sessionService.setCurrentContract(defaultContract);
  }

  private listenForUserRole() {
    this.userRoleSubscription = this.store
      .select('userRole')
      .pipe(filter((value: any) => value !== undefined))
      .subscribe((value: any) => {
        this.canUseChat =
          value === USER_ROLES.READONLY ? false : this.canUseChat;
        this.canTermsConditions = value === USER_ROLES.STANDARD;
        this.userCanUseAccountManager = value === USER_ROLES.STANDARD;
        this.canUseTermsAndCondition();
      });
  }

  private cleanUrl() {
    const [cleanUrl] = this.router.url.split('?');
    this.router.navigate([cleanUrl], { relativeTo: this.route });
  }

  private scanForMaintenanceMode() {
    this.store
      .select(selectFeatureFlag('maintenanceModeHB'))
      .pipe(
        filter(
          (maintenanceMode: any) =>
            maintenanceMode !== undefined &&
            this.isMaintenanceMode !== maintenanceMode,
        ),
      )
      .subscribe((maintenanceMode: any) => {
        this.isMaintenanceMode = maintenanceMode;
        if (maintenanceMode) {
          this.ngOnDestroy();
        } else {
          this.initListeners();
        }
      });
  }

  private scanForenableGeolocationOperations() {
    this.enableGeolocationOperationsFeatureFlag$ =
      this.geolocationService.shouldRestricGeolocationRelatedOperations$;
  }

  private scanForServiceInterruptions() {
    this.isServiceInterrupted$ = this.store.select('serviceInterruption').pipe(
      filter((state) => state !== undefined),
      map((e: any) => e.toJS()),
      map(
        ({
          position,
          buyingPower,
        }: {
          position: boolean;
          buyingPower: boolean;
        }) => position || buyingPower,
      ),
      tap(() => (this.isBannerVisible = true)),
    );
  }

  private scanForPriceAlerts() {
    this.openPriceAlerts$ = this.store.select('openPriceAlerts').pipe(
      map(Boolean),
      switchMap((state) =>
        this.store.select(selectFeatureFlag('priceAlerts')).pipe(
          filter((stateShadowed) => stateShadowed !== undefined),
          map((enablePriceAlerts) => !!(enablePriceAlerts && state)),
        ),
      ),
    );
  }

  private backupRouteSnapshot(): void {
    this.queryParamsSnapshot = this.route.snapshot.queryParams;
  }

  private handleDashboard(accessToken: string): void {
    const currentSession = this.storageService.getItem('session', true);
    const userChanged = this.sessionService.didUserChange(
      accessToken,
      currentSession && currentSession.sessionToken,
    );
    if (userChanged) {
      this.storageService.clearOne('has_dashboard');
    }
    this._userIsComingFromDashboard = this.hasDashboard();
  }

  private hasDashboard(): boolean {
    if (this.queryParamsSnapshot.contract_id) {
      this.storageService.setItem('has_dashboard', true);
      return true;
    } else {
      return !!this.storageService.getItem('has_dashboard');
    }
  }

  private listenElPatron() {
    this.logoutSubscription = this.store
      .select('elPatron')
      .pipe(
        map((value: any) => value.toJS()),
        filter((value: any) => value.showIframe === true),
      )
      .subscribe(() => {
        this.silentAuth.init(this.elPatronData.SILENT_URL);
        this.sessionService.showSilentIframe(false);
      });
  }

  private startAuthenticationProcess() {
    this.listenElPatron();
    const tokenFromUrl = this.queryParamsSnapshot.access_token;

    const localStorageSession =
      this.storageService.getItem('session', true) || {};
    const accessToken = tokenFromUrl || localStorageSession.sessionToken;
    const idToken =
      this.queryParamsSnapshot.id_token || localStorageSession.idToken;

    const isTokenExpired = accessToken
      ? this.sessionService.isTokenExpired(accessToken)
      : true;
    if (!isTokenExpired) {
      this.authCallback(accessToken, idToken);
    } else {
      this.silentAuth.init(this.elPatronData.SILENT_URL);
    }
  }

  private authCallback(accessToken: string, identityToken?: string) {
    if (accessToken) {
      this.handleDashboard(accessToken);
      this.sessionService.startExpirationInterval(accessToken);
      this.sessionService.setAuthorizationHeader(accessToken); // Paste accessToken to requests header
      this.sessionService.setIdTokenSession(identityToken);
      this.sessionService.login(accessToken, identityToken); // Do login process
      identityToken && this.setSessionDotCom(identityToken);
    } else {
      this.redirectToPatron();
    }
  }

  private getAgreementLog() {
    const agreementTypeId = 17;
    this.operationService.getAgreementLog(agreementTypeId).subscribe(
      (value: any) => {
        if (value.length) {
          this.contractsToAddTerms = [];
          this.legacyContracts.forEach((contract: string) => {
            const foundContract = value.find(
              (item: any) => item.contractId === contract,
            );
            !foundContract && this.contractsToAddTerms.push(contract);
          });
        } else {
          this.contractsToAddTerms = this.legacyContracts;
        }
        this.showTermsAndConditions = !!this.contractsToAddTerms.length;
      },
      (error) => this.showError(error),
    );
  }

  private scanForAllContracts() {
    combineLatest(
      this.store
        .select('contracts')
        .pipe(map((contracts: any) => contracts.toJS())),
      this.store.select('loremAccounts').pipe(
        map((value: any) => value.toJS()),
        distinctUntilChanged((a, b) => isEqual(a, b)),
      ),
    ).subscribe(
      ([contracts, loremAccounts]) => {
        if (loremAccounts.loaded) {
          this.legacyContracts = [];
          contracts = contracts.filter((item: any) => {
            return (
              item.legalPersonalityId ===
                LEGAL_PERSONALITY_TYPE.PHYSICAL_PERSON && !item.isDiscretional
            );
          });
          contracts.forEach((contract: any) => {
            const loremAccount = this.contractsService.isLoremAccount(
              loremAccounts.data,
              contract.contractId,
            );
            (!loremAccount || loremAccount.is_migrated) &&
              this.legacyContracts.push(contract.contractId);
          });
          this.legacyContracts.length && this.canUseTermsAndCondition();
        }
      },
      () => {
        // Do nothing, just catch error
      },
    );
  }

  private canUseTermsAndCondition() {
    if (featureFlags.isAccountManagerEnableToLegacy.isEnabled()) {
      this.showTermsAndConditions = false;
      this.legacyContracts.length &&
        this.canTermsConditions &&
        this.getAgreementLog();
    }
  }

  private reportStatus(status: boolean) {
    this.store.dispatch({
      type: USER_HAS_NO_BANK_ACCOUNT,
      payload: status,
    });
  }

  private getWithDrawalAccount(contractID: string) {
    this.operationService
      .getWithdrawalAccounts(contractID)
      .pipe(retry(2))
      .subscribe(
        (result: BankAccountViewModel[]) => {
          if (result.length) {
            const userHasBankAccountIssues = !!result.find(
              (account: BankAccountViewModel) => account.isValidAccount,
            );
            this.reportStatus(userHasBankAccountIssues);
          } else {
            this.reportStatus(false);
          }
        },
        () => {
          this.reportStatus(false);
          // Just catch error
        },
      );
  }

  private scanForWithdrawalAccount(contractID: string) {
    if (
      featureFlags.isAccountManagerEnableToLegacy.isEnabled() &&
      this.currentContract.legalPersonalityType ===
        LEGAL_PERSONALITY_TYPE.PHYSICAL_PERSON &&
      this.userCanUseAccountManager
    ) {
      this.getWithDrawalAccount(contractID);
    } else {
      this.isLoremAccount
        ? this.getWithDrawalAccount(contractID)
        : this.reportStatus(false);
    }
  }

  private setSessionDotCom(identityToken: string): void {
    this.sessionService.setSessionDotCom(identityToken).subscribe(
      () => {},
      () => {},
    );
  }

  private getDayTrades() {
    this.operationService
      .getDayTrades(
        this.selectedAccount.contractId,
        this.selectedAccount.accountId,
      )
      .subscribe(() => {});
  }

  private listenerDayTrades() {
    this.initializedDayTrades = true;

    this.dayTradesSubscription = this.store
      .select('dayTrades')

      .pipe(
        filter(
          (value: any) => value !== undefined && value.nextAvailableDayTrade,
        ),
      )
      .subscribe((items: DayTradesViewModel) => {
        this.showDayTrades = this.isUsaContract && items.showDayTrade();
        this.dayTrades = items.dayTrades;
        this.dayTradeNextDate = items.nextAvailableDayTrade;
      });

    this.ordersSubscription = this.store
      .select('blotter')
      .pipe(
        map((values: any) => values.get('capitals')),
        filter((values: any) => values !== undefined),
        distinctUntilChanged(),
      )
      .subscribe((value: any) => {
        this.isUsaContract && this.getDayTrades();
      });
  }

  private getContractAccount() {
    this.contractAccountSubscription = this.store
      .select(selectContractsAccountsAccounts)
      .pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
      .subscribe((accounts: any) => {
        if (
          accounts &&
          accounts.hasOwnProperty(this.currentContract.contractID)
        ) {
          this.selectedAccount = accounts[this.currentContract.contractID];
          this.isUsaContract &&
            !this.initializedDayTrades &&
            this.listenerDayTrades();
        }
      });
  }

  /**
   * Check is the currentContract can activate the navigated route
   *
   * This could be done with a combination of Route Data + Route Guards,
   * but we couldn't make it work so we subscribe to Router events, and check
   * against an static array of available routes.
   *
   * @see {@link RoutesAvailableForUsa}
   */
  private checkIfRouteCanBeActivated() {
    combineLatest(
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
      ),
      this.store.select(selectIsUsa),
    ).subscribe(([event, isUsa]: [NavigationEnd, boolean]) => {
      const { urlAfterRedirects: url } = event;
      if (isUsa && RoutesAvailableForUsa.indexOf(url) === -1) {
        this.router.navigateByUrl(RoutesAvailableForUsa[0]);
      }
    });
  }

  private scanForTradingUsaOperation() {
    this.store
      .select(selectFeatureFlag('tradingUsaOperation'))
      .pipe(
        filter(
          (enableTradingUsaOperation: any) =>
            enableTradingUsaOperation !== undefined,
        ),
      )
      .subscribe((tradingUsaOperation: any) => {
        this.tradingUsaOperation = tradingUsaOperation;
        this.setTradingUsaOperation();
      });
  }

  private setTradingUsaOperation() {
    this.disabledTradingUsaOperation =
      this.isUsaContract && !this.tradingUsaOperation;
    this.setBannerMessage();
  }

  private setBannerMessage(): void {
    this.subscriptions.add(
      this.store
        .select('opening')
        .pipe(
          filter((value) => value !== undefined),
          map((value) => value.toJS() as Opening),
          filter((value) => value.openingType !== undefined),
        )
        .subscribe((openingValue) => {
          this.bannerMessage =
            openingValue.openingType === OPENING_TYPE.light &&
            !this.storageService.getItem('is_upgrade_banner_closed')
              ? this.tradingGlobalMessage
              : this.isUsaContract && this.disabledTradingUsaOperation
              ? this.disabledTradingUsaMessage
              : this.disabledTradingMessage;

          this.isTradingGlobalBannerVisible =
            openingValue.openingType === OPENING_TYPE.light &&
            !this.storageService.getItem('is_upgrade_banner_closed');

          this.isBannerVisible =
            this.isTradingGlobalBannerVisible ||
            this.disabledTradingUsaOperation;
        }),
    );
  }

  private handleUpgradeBlackLink(): void {
    this.store
      .select('config')
      .pipe(
        filter((mapShadowed: any) => MapInmutable.isMap(mapShadowed)),
        map((mapShadowed: any) => mapShadowed.toJS()),
        first(),
      )
      .subscribe(({ comBlackUpgrade }) => {
        if (this.upgradeLink) {
          const { nativeElement } = this.upgradeLink;
          nativeElement.href = comBlackUpgrade;
          nativeElement.dispatchEvent(new MouseEvent('click'));
        }
      });
  }
}

interface ScrollStorePayload {
  toJS?: () => {
    block: boolean;
    source: string;
  };
}

/**
 * These routes are used to check if the current route can
 * be activated for USA contracts.
 *
 * This should be done either with a canActivate guard, and/or
 * with `data` inside each route in {@link routes}.
 *
 * For example:
 *
 * ```
 * children: [
 *    {
 *       path: 'mercados',
 *       data: { isAvailableToUsa: true }
 *     },
 *     {
 *       path: 'funds',
 *       data: { isAvailableToUsa: false }
 *     },
 *   ]
 * ```
 *
 * But `data` is not being read successfully on route changes, and
 * we don't know of ways to trigger `canActivate` guards at will from
 * components.
 *
 * So we create a list of routes that a USA contract CAN access, and
 * use it to filter every visited route in
 * @see {@link MainContainerComponent.checkIfRouteCanBeActivated } .
 */
const RoutesAvailableForUsa = [
  '/main/portfolio',
  '/main/mercados',
  '/main/ordenes/historial',
  '/main/herramientas/rendimientos',
];

interface CustomInfoBannerFF {
  icon: string;
  message: string;
  display: ('trading' | 'trading_global' | 'all')[];
}
